import { loadFlashPlan, parseFlashPlan, type FlashPlan } from "idfweb-build";
import { chipLabel, chipNameOfTarget, type ChipIdentity } from "idfweb-chip";
import { SerialDecoder, Symbols } from "idfweb-panic";
import type { PresetChoice } from "idfweb-runtime-protocol";
import type { MessageLevel, Reporter, Task } from "idfweb-tasks";
import { Errno, WasiError } from "idfweb-vfs";
import { showMenu } from "idfweb-workbench";

import type { Board, BoardEvents, ConnectContext, Connector, RememberedBoard } from "./board.ts";
import { SerialText } from "./serial-text.ts";
import { AUTO, autoTarget, decideOnBoard, effectiveTarget, type IdfTargets, type Selection } from "./target.ts";
import { createTerminal, type PaneTerminal } from "./terminal.ts";

import "./device.css";

/**
 * Device pane managing the connected board, serial monitor, chip target
 * selection, and flashing. The board is reached through one of the page's
 * connectors (`board.ts`). The terminal shows what the board sends and
 * nothing else; what the pane itself has to say goes to the page's toasts.
 */
export interface DevicePane {
    /**
     * The pane's controls, which the page places in its toolbar: the chip or
     * preset dropdown, the board's buttons, and which board is connected.
     */
    readonly controls: { target: HTMLElement; connection: HTMLElement; status: HTMLElement };
    /** Write the built images to the connected board and watch it boot. */
    flash(): Promise<void>;
    /** The targets ESP-IDF supports, once known. Enables the dropdown. */
    setTargets(targets: IdfTargets): void;
    /** The project set its chip: select it, without starting the toolchain download. */
    selectTarget(target: string): void;
    /** The chip a build is for now: the selection, or the default under "auto". */
    target(): string;
    /**
     * The project's configure presets and the one builds use, which replace
     * the target dropdown with a preset dropdown; undefined brings it back.
     */
    setPresets(presets: PresetChoice[] | undefined, selected: string | undefined): void;
    /**
     * What the board sent since the previous call or the start of the last
     * flash, at most the last `SERIAL_KEPT` bytes.
     */
    takeSerialOutput(): string;
    /** Reset the board into its application, as the Reset button does. */
    reset(): Promise<void>;
    /** Write `text` to the board's console. */
    send(text: string): Promise<void>;
    /** Show a line of an agent's monitor script in the terminal. */
    note(line: string): void;
    /** Call `listener` with the output read_serial keeps, as it arrives, until the returned function is called. */
    watchSerial(listener: (text: string) => void): () => void;
    /** How the connected board was reached, or undefined while none is. */
    remembered(): RememberedBoard | undefined;
    /**
     * Connect the board `remembered` describes, as after a reload, when no
     * board is connected and its connector finds that board without a picker.
     */
    reconnect(remembered: RememberedBoard): void;
    /** End the board's connection, as the Disconnect button does, unless the board is busy. */
    disconnect(): void;
    /** The connected board, also while it is away and waited for. */
    board(): Board | undefined;
    /**
     * Read the connected board's storage, with the toolbar disabled meanwhile,
     * as a task titled `title`; see `Board.readDisk`.
     */
    readDisk(disk: string, offset: number, length: number, title?: string): Promise<Uint8Array>;
    /** Write the connected board's storage; see `Board.writeDisk`. */
    writeDisk(disk: string, offset: number, data: Uint8Array): Promise<void>;
    dispose(): void;
}

export interface DevicePaneDeps {
    /** The ways to connect a board, in the order the Connect menu lists them. */
    connectors: Connector[];
    /** Bytes from the guest filesystem, which is stored in another thread. */
    readFiles(paths: string[]): Promise<Uint8Array[]>;
    /** Where the pane reports a flash and what it has to say. The page shows it as toasts. */
    reporter: Reporter;
    /** The build directory a flash writes from, which the worker chooses. */
    buildDir(): string;
    /**
     * The chip every build from now on is for. `download` is whether to fetch
     * its toolchain now: a person's choice does, a project's does not.
     */
    setTarget(target: string, download: boolean): void;
    /** The configure preset every build from now on uses; a person chose it. */
    setPreset(name: string): void;
    /** Where the serial monitor goes; the pane's root holds its dialogs. */
    terminalHost: HTMLElement;
    /** The person connected a board, or disconnected one: what a reload reconnects changed. */
    rememberedChanged?(remembered: RememberedBoard | undefined): void;
    /** Called whenever the toolbar is redrawn, with whether a board is connected. */
    connectionChanged?(connected: boolean): void;
    /** Called when a flash starts, from Run or from an agent; the page shows the monitor. */
    flashStarted?(): void;
    /** Called when a flash ends, written or not (`failure` says why): what the board holds may have changed. */
    flashEnded?(failure: string | undefined): void;
}

export function createDevicePane(root: HTMLElement, deps: DevicePaneDeps): DevicePane {
    return new Pane(root, deps);
}

const ROOT_CLASS = "idfweb-device";

const SERIAL_KEPT = 64 * 1024;

/** How long an agent's note waits for the board to end its line. */
const NOTE_WAIT_MS = 500;

const NOT_CONNECTED =
    "No device is connected. Press Connect in the toolbar and choose a board's" +
    " serial port or the emulator — the browser only opens the port picker from a click.";

class Pane implements DevicePane {
    readonly controls: DevicePane["controls"];
    readonly #root: HTMLElement;
    readonly #deps: DevicePaneDeps;
    readonly #terminal: PaneTerminal;
    readonly #name: HTMLSpanElement;
    readonly #status: HTMLSpanElement;
    readonly #targetSelect: HTMLSelectElement;
    readonly #presetSelect: HTMLSelectElement;
    readonly #connectButton: HTMLButtonElement;
    readonly #resetButton: HTMLButtonElement;
    readonly #disconnectButton: HTMLButtonElement;

    /** The connected board, which may be waiting to come back. */
    #board: Board | undefined;
    /** The connector that reached `#board`. */
    #connector: Connector | undefined;
    /** Indicates an active operation on the board; disables toolbar buttons. */
    #busy = false;
    #flashing = false;
    #selection: Selection = AUTO;
    #targets: IdfTargets | undefined;
    /** The project's configure presets while it has any; the preset dropdown shows them. */
    #presets: PresetChoice[] | undefined;
    /** Chip detected prior to target index arrival; applied in setTargets. */
    #pendingChip: ChipIdentity | undefined;
    #serialChunks: Uint8Array[] = [];
    /** Decodes addresses in the monitor's output; read from the ELF file once per connection or flash. */
    #decoder: Promise<SerialDecoder> | undefined;
    /** Board output, in order, while the decoder is still being read. */
    #output: Promise<void> = Promise.resolve();
    #serialBytes = 0;
    readonly #watchers = new Set<(text: string) => void>();
    readonly #watchText = new SerialText();
    /** Whether the terminal's last line from the board has no line end yet. */
    #lineOpen = false;
    /** Agent notes that wait for the board to end its line. */
    #notes: string[] = [];
    #notesTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(root: HTMLElement, deps: DevicePaneDeps) {
        this.#root = root;
        this.#deps = deps;
        root.classList.add(ROOT_CLASS);

        this.#name = document.createElement("span");
        this.#name.className = "idfweb-device-name";
        this.#status = document.createElement("span");
        this.#status.className = "codicon idfweb-device-status";

        this.#targetSelect = targetSelect();
        this.#targetSelect.addEventListener("change", () => this.#select(this.#targetSelect.value));
        this.#presetSelect = presetSelect();
        this.#presetSelect.addEventListener("change", () => this.#choosePreset(this.#presetSelect.value));
        this.#connectButton = button("Connect", () => this.#chooseConnector(), "plug");
        this.#connectButton.append(glyph("chevron-down"));
        this.#connectButton.title = "Connect a board or the emulator";
        this.#connectButton.setAttribute("aria-haspopup", "menu");
        this.#resetButton = iconButton("debug-restart", "Reset the board", () => this.#start(() => this.#reset()));
        this.#disconnectButton = iconButton("debug-disconnect", "Disconnect", () =>
            this.#start(() => this.#disconnect()),
        );

        this.controls = {
            target: group(this.#targetSelect, this.#presetSelect),
            connection: group(this.#connectButton, this.#resetButton, this.#disconnectButton),
            status: group(this.#status, this.#name),
        };
        this.controls.status.classList.add("idfweb-device-state");

        const host = deps.terminalHost;
        host.classList.add("idfweb-device-terminal");

        this.#terminal = createTerminal(host);
        this.#render();
    }

    setTargets(targets: IdfTargets): void {
        this.#targets = targets;
        fillTargets(this.#targetSelect, targets);
        this.#targetSelect.value = this.#selection;
        this.#render();
        const chip = this.#pendingChip;
        this.#pendingChip = undefined;
        // Apply detected chip once target list is available.
        if (chip !== undefined) void this.#adopt(chip, targets);
    }

    selectTarget(target: string): void {
        this.#select(target, false);
    }

    target(): string {
        return effectiveTarget(this.#selection);
    }

    setPresets(presets: PresetChoice[] | undefined, selected: string | undefined): void {
        this.#presets = presets?.length ? presets : undefined;
        const chosen = this.#presets?.find((each) => each.name === selected);
        this.#targetSelect.hidden = chosen?.fixed === true;
        this.#presetSelect.hidden = this.#presets === undefined;
        this.#presetSelect.replaceChildren(
            ...(this.#presets ?? []).map((each) =>
                option(each.name, each.fixed ? `${each.label} – ${chipNameOfTarget(each.target)}` : each.label),
            ),
        );
        if (chosen !== undefined) {
            this.#presetSelect.value = chosen.name;
            // The worker took the preset's chip already; nothing to send back.
            this.#selection = chosen.target;
            if (!hasOption(this.#targetSelect, chosen.target)) {
                this.#targetSelect.append(option(chosen.target, chipNameOfTarget(chosen.target)));
            }
            this.#targetSelect.value = chosen.target;
        }
        this.#dropUnfitBoard();
        this.#render();
    }

    takeSerialOutput(): string {
        const bytes = new Uint8Array(this.#serialBytes);
        let at = 0;
        for (const chunk of this.#serialChunks) {
            bytes.set(chunk, at);
            at += chunk.length;
        }
        this.#serialChunks = [];
        this.#serialBytes = 0;
        return new SerialText().decode(bytes, true);
    }

    async reset(): Promise<void> {
        await this.#forAgent(async (board) => {
            this.#say("info", "Resetting the board.");
            await board.reset();
        });
    }

    async send(text: string): Promise<void> {
        await this.#forAgent((board) => board.write(new TextEncoder().encode(text)));
    }

    note(line: string): void {
        this.#notes.push(`\x1b[33m--- agent: ${line}\x1b[0m`);
        if (!this.#lineOpen) this.#writeNotes();
        // A prompt waits for input and may never end its line.
        else this.#notesTimer ??= setTimeout(() => this.#writeNotes(), NOTE_WAIT_MS);
    }

    #writeNotes(): void {
        clearTimeout(this.#notesTimer);
        this.#notesTimer = undefined;
        if (this.#notes.length === 0) return;
        if (this.#lineOpen) this.#terminal.write("\r\n");
        this.#lineOpen = false;
        for (const note of this.#notes) this.#terminal.write(`${note}\r\n`);
        this.#notes = [];
    }

    watchSerial(listener: (text: string) => void): () => void {
        this.#watchers.add(listener);
        return () => this.#watchers.delete(listener);
    }

    remembered(): RememberedBoard | undefined {
        return this.#board?.remembered;
    }

    reconnect(remembered: RememberedBoard): void {
        const connector = this.#deps.connectors.find((each) => each.id === remembered.connector);
        if (connector === undefined || connector.unavailable !== undefined || this.#board !== undefined) return;
        if (!this.#offered().includes(connector)) return;
        this.#start(async () => {
            // A click may have connected a board meanwhile.
            if (this.#board !== undefined) return;
            try {
                await this.#connect(connector, (context) => connector.reconnect(remembered, context));
            } catch (e) {
                this.#say("warn", `Could not connect the board again: ${message(e)}`);
            }
        });
    }

    disconnect(): void {
        this.#start(() => this.#disconnect());
    }

    /** Runs an agent's action on the connected board, with the toolbar disabled meanwhile. */
    async #forAgent(what: (board: Board) => Promise<void>): Promise<void> {
        if (this.#busy) throw new Error("The device pane is busy with the board.");
        const board = this.#board;
        if (board === undefined || !board.ready) throw new Error(NOT_CONNECTED);
        this.#busy = true;
        this.#render();
        try {
            await what(board);
        } finally {
            this.#idle();
        }
    }

    board(): Board | undefined {
        return this.#board;
    }

    async readDisk(disk: string, offset: number, length: number, title?: string): Promise<Uint8Array> {
        return await this.#onDisk(title ?? `Reading the ${disk} of ${this.#board?.label}`, (board, task) => {
            if (board.readDisk === undefined) throw new Error(`${board.label} cannot read its storage.`);
            return board.readDisk(disk, offset, length, task);
        });
    }

    async writeDisk(disk: string, offset: number, data: Uint8Array): Promise<void> {
        await this.#onDisk(`Writing the ${disk} of ${this.#board?.label}`, (board, task) => {
            if (board.writeDisk === undefined) throw new Error(`${board.label} cannot write its storage.`);
            return board.writeDisk(disk, offset, data, task);
        });
    }

    /** Run a disk operation as a task the page shows, with the toolbar disabled meanwhile. */
    async #onDisk<T>(title: string, what: (board: Board, task: Task) => Promise<T>): Promise<T> {
        if (this.#busy) throw new Error("The device pane is busy with the board.");
        const board = this.#board;
        if (board === undefined || !board.ready) throw new Error(NOT_CONNECTED);
        this.#busy = true;
        this.#render();
        const task = this.#deps.reporter.task(title, { unit: "bytes" });
        try {
            const result = await what(board, task);
            task.done();
            return result;
        } catch (e) {
            task.failed(message(e));
            throw e;
        } finally {
            this.#idle();
        }
    }

    async flash(): Promise<void> {
        if (this.#flashing) this.#refuse("A flash is already running.");
        if (this.#busy) this.#refuse("The device pane is busy with the board.");
        const board = this.#board;
        if (board === undefined || !board.ready) this.#refuse(NOT_CONNECTED);

        this.#flashing = true;
        this.#busy = true;
        this.#render();
        this.#deps.flashStarted?.();
        this.takeSerialOutput();
        const task = this.#deps.reporter.task(`Flashing ${board.label}`, { unit: "bytes" });
        let failure: string | undefined;
        try {
            task.progress(0, { detail: "reading the images out of the build directory" });
            const plan = await this.#plan();
            // The new ELF file decodes what the board prints from now on.
            this.#decoder = undefined;
            await board.flash(plan, task);
            task.done();
        } catch (e) {
            failure = message(e);
            task.failed(failure);
            throw e instanceof Error ? e : new Error(failure);
        } finally {
            this.#flashing = false;
            this.#idle();
            this.#deps.flashEnded?.(failure);
        }
    }

    async #plan(): Promise<FlashPlan> {
        const buildDir = this.#deps.buildDir();
        const json = await this.#readText(`${buildDir}/flasher_args.json`);
        return loadFlashPlan(parseFlashPlan(json, buildDir), (paths) => this.#deps.readFiles(paths));
    }

    dispose(): void {
        const board = this.#board;
        this.#board = undefined;
        this.#connector = undefined;
        // Close the board asynchronously without blocking dispose.
        void board?.close();
        this.#terminal.dispose();
        this.#root.classList.remove(ROOT_CLASS);
        for (const control of Object.values(this.controls)) control.remove();
    }

    // ---- the buttons -------------------------------------------------------

    /** Runs an asynchronous button action with toolbar disabled. */
    #start(what: () => Promise<void>): void {
        if (this.#busy) return;
        this.#busy = true;
        this.#render();
        void what()
            .catch((e: unknown) => this.#say("error", message(e)))
            .finally(() => this.#idle());
    }

    #idle(): void {
        this.#busy = false;
        this.#render();
    }

    /** The Connect menu, under its button. */
    #chooseConnector(): void {
        const rect = this.#connectButton.getBoundingClientRect();
        showMenu(
            { clientX: rect.left, clientY: rect.bottom + 2 },
            this.#offered().map((connector) => ({
                label: connector.label,
                disabled: connector.unavailable,
                // The click on the item is the gesture a port picker needs.
                run: () => this.#start(() => this.#connect(connector)),
            })),
        );
    }

    /** The connectors the Connect menu lists for the current target. */
    #offered(): Connector[] {
        const target = this.target();
        return this.#deps.connectors.filter((each) => each.targets?.includes(target) ?? true);
    }

    /**
     * Disconnect a board whose connector does not run the new target, as the
     * Connect menu no longer lists it. Its next flash would fail anyway.
     */
    #dropUnfitBoard(): void {
        const board = this.#board;
        if (board === undefined || this.#connector === undefined) return;
        if (this.#offered().includes(this.#connector)) return;
        this.#board = undefined;
        this.#connector = undefined;
        void board.close();
        this.#deps.rememberedChanged?.(undefined);
        this.#say("info", `Disconnected ${board.label}: it does not run ${chipNameOfTarget(this.target())} builds.`);
        this.#render();
    }

    /** Connect through `connector` from a click, or with `how` in its place. */
    async #connect(
        connector: Connector,
        how: (context: ConnectContext) => Promise<Board | undefined> = (context) => connector.connect(context),
    ): Promise<void> {
        let connected: Board | undefined;
        const events: BoardEvents = {
            data: (bytes) => this.#show(bytes),
            changed: () => {
                // A board that came back or changed chip starts a new boot log.
                this.#decoder = undefined;
                this.#render();
            },
            lost: (reason) => {
                if (connected === undefined || this.#board !== connected) return;
                this.#board = undefined;
                this.#connector = undefined;
                this.#say("warn", reason);
                this.#render();
            },
        };
        this.#decoder = undefined;
        connected = await how({
            events,
            reporter: this.#deps.reporter,
            chipFound: (chip) => this.#chipFound(chip),
            target: () => this.target(),
        });
        if (connected === undefined) return;
        this.#board = connected;
        this.#connector = connector;
        this.#deps.rememberedChanged?.(connected.remembered);
    }

    /** Apply a chip the board reported. False means do not connect. */
    async #chipFound(chip: ChipIdentity): Promise<boolean> {
        const targets = this.#targets;
        if (targets === undefined) {
            this.#say("info", `Detected ${chipLabel(chip)}. The target is set once ESP-IDF is unpacked.`);
            this.#pendingChip = chip;
            return true;
        }
        if (await this.#adopt(chip, targets)) return true;
        this.#say("info", "Not connected.");
        return false;
    }

    /** Apply the detected chip to the selection. False means do not connect. */
    async #adopt(chip: ChipIdentity, targets: IdfTargets): Promise<boolean> {
        const presets = this.#presets;
        const current = presets?.find((each) => each.name === this.#presetSelect.value);
        if (presets !== undefined && current?.fixed) return this.#adoptPreset(chip, current, presets);
        const decision = decideOnBoard(this.#selection, chip.name, targets);
        switch (decision.kind) {
            case "unknown":
                this.#say(
                    "warn",
                    `Detected ${chipLabel(chip)}, which this ESP-IDF has no target for. The target stays` +
                        ` ${chipNameOfTarget(effectiveTarget(this.#selection))}.`,
                );
                return true;
            case "keep":
                this.#say("info", `Detected ${chipLabel(chip)}.`);
                return true;
            case "adopt":
                this.#say("info", `Detected ${chipLabel(chip)}.`);
                this.#select(decision.target);
                return true;
            case "confirm": {
                const agreed = await confirmTargetChange(this.#root, chipLabel(chip), decision.from, decision.to);
                if (!agreed) return false;
                this.#select(decision.to);
                return true;
            }
        }
    }

    /**
     * Apply the detected chip when the chosen preset sets its chip: offer the
     * first preset that sets the board's. False means do not connect.
     */
    async #adoptPreset(chip: ChipIdentity, current: PresetChoice, presets: PresetChoice[]): Promise<boolean> {
        const target = chip.target;
        if (current.target === target) {
            this.#say("info", `Detected ${chipLabel(chip)}.`);
            return true;
        }
        const match = presets.find((each) => each.fixed && each.target === target);
        if (match === undefined) {
            this.#say(
                "warn",
                `Detected ${chipLabel(chip)}, which no preset names. The preset stays ${current.label};` +
                    " add one for this chip in CMakePresets.json.",
            );
            return true;
        }
        const text =
            `The board is ${chipLabel(chip)}, and the preset ${current.label} builds for` +
            ` ${chipNameOfTarget(current.target)}. Switch to the preset ${match.label}?`;
        if (!(await confirm(this.#root, text, `Switch to ${match.label}`))) return false;
        this.#choosePreset(match.name);
        return true;
    }

    /** Make a preset the one builds use; the worker answers with the new state. */
    #choosePreset(name: string): void {
        this.#presetSelect.value = name;
        this.#deps.setPreset(name);
        const label = this.#presets?.find((each) => each.name === name)?.label ?? name;
        this.#say("info", `The preset is now ${label}.`);
    }

    /** Updates target selection and notifies worker runtime if changed. */
    #select(selection: Selection, download = true): void {
        const before = effectiveTarget(this.#selection);
        this.#selection = selection;
        // Ensure unknown target option exists in select element.
        if (!hasOption(this.#targetSelect, selection)) {
            this.#targetSelect.append(option(selection, chipNameOfTarget(selection)));
        }
        this.#targetSelect.value = selection;
        const after = effectiveTarget(selection);
        if (after === before) return;
        this.#deps.setTarget(after, download);
        this.#say("info", `The target is now ${chipNameOfTarget(after)}; the build directory is emptied.`);
        this.#dropUnfitBoard();
    }

    async #reset(): Promise<void> {
        const board = this.#board;
        if (board === undefined || !board.ready) return;
        this.#say("info", "Resetting the board.");
        await board.reset();
    }

    async #disconnect(): Promise<void> {
        const board = this.#board;
        if (board === undefined) return;
        const ready = board.ready;
        this.#board = undefined;
        this.#connector = undefined;
        this.#deps.rememberedChanged?.(undefined);
        await board.close();
        this.#say("info", ready ? "Disconnected." : "Stopped waiting for the device.");
    }

    // ---- the monitor -------------------------------------------------------

    /** Passes board output to the terminal and to read_serial, with decoded addresses after their lines. */
    #show(bytes: Uint8Array): void {
        const decoder = (this.#decoder ??= this.#loadDecoder());
        this.#output = this.#output.then(async () => {
            for (const piece of (await decoder).push(bytes)) {
                this.#terminal.write(piece);
                const kept = typeof piece === "string" ? new TextEncoder().encode(piece) : piece;
                this.#keepSerial(kept);
                if (kept.length > 0) this.#lineOpen = kept[kept.length - 1] !== 0x0a;
                if (!this.#lineOpen) this.#writeNotes();
                if (this.#watchers.size === 0) continue;
                const text = this.#watchText.decode(kept);
                for (const watcher of this.#watchers) watcher(text);
            }
        });
    }

    /** The build directory's ELF file, which is what the last flash from this page wrote. */
    async #loadDecoder(): Promise<SerialDecoder> {
        let elf: Uint8Array | undefined;
        const buildDir = this.#deps.buildDir();
        try {
            const [json] = await this.#deps.readFiles([`${buildDir}/project_description.json`]);
            const description = JSON.parse(new TextDecoder().decode(json)) as { app_elf?: string };
            if (description.app_elf === undefined) return new SerialDecoder(undefined);
            [elf] = await this.#deps.readFiles([`${buildDir}/${description.app_elf}`]);
        } catch {
            // Nothing built yet, so nothing to decode against.
            return new SerialDecoder(undefined);
        }
        if (elf === undefined) return new SerialDecoder(undefined);
        try {
            const symbols = new Symbols(elf);
            if (symbols.debugInfoProblem !== undefined) {
                this.#say("warn", `Addresses are decoded without source lines: ${symbols.debugInfoProblem}.`);
            }
            const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", elf.slice()));
            const sha256 = Array.from(digest, (b) => b.toString(16).padStart(2, "0")).join("");
            return new SerialDecoder(symbols, sha256);
        } catch (e) {
            this.#say("warn", `Addresses are not decoded: ${message(e)}`);
            return new SerialDecoder(undefined);
        }
    }

    #keepSerial(bytes: Uint8Array): void {
        this.#serialChunks.push(bytes);
        this.#serialBytes += bytes.length;
        while (this.#serialBytes - this.#serialChunks[0]!.length >= SERIAL_KEPT) {
            this.#serialBytes -= this.#serialChunks.shift()!.length;
        }
    }

    #say(level: MessageLevel, text: string): void {
        this.#deps.reporter.message(level, text);
    }

    /** Says why a flash cannot start, since nothing else shows it, and gives up. */
    #refuse(text: string): never {
        this.#say("error", text);
        throw new Error(text);
    }

    async #readText(path: string): Promise<string> {
        try {
            const [bytes] = await this.#deps.readFiles([path]);
            return new TextDecoder().decode(bytes);
        } catch (e) {
            if (e instanceof WasiError && e.errno === Errno.NOENT) {
                throw new Error(`${path} is not there. Build the project before flashing it.`);
            }
            throw e;
        }
    }

    // ---- the toolbar -------------------------------------------------------

    #render(): void {
        const board = this.#board;
        const connected = board?.ready === true;
        const waiting = board !== undefined && !connected;
        this.#name.textContent = board?.label ?? "no device";
        this.#name.classList.toggle("idfweb-device-idle", !connected);
        this.controls.status.title =
            board?.detail === undefined ? this.#name.textContent : `${this.#name.textContent}\n${board.detail}`;
        this.#targetSelect.disabled = this.#busy || this.#targets === undefined;
        this.#presetSelect.disabled = this.#busy;
        this.#connectButton.disabled = this.#busy || this.#offered().every((each) => each.unavailable !== undefined);
        this.#connectButton.hidden = board !== undefined;
        this.#resetButton.disabled = !connected || this.#busy;
        this.#resetButton.hidden = !connected;
        this.#disconnectButton.disabled = this.#busy;
        this.#disconnectButton.hidden = board === undefined;
        const status = connected ? "circle-filled" : waiting ? "sync" : "circle-outline";
        this.#status.className = `codicon idfweb-device-status codicon-${status}`;
        this.#status.classList.toggle("connected", connected);
        this.#deps.connectionChanged?.(connected);
    }
}

/** Prompts user to confirm changing build target to match detected board. */
function confirmTargetChange(root: HTMLElement, chip: string, from: string, to: string): Promise<boolean> {
    const text =
        `The board is ${chip}, and the target is set to ${chipNameOfTarget(from)}. ` +
        `Change the target to ${chipNameOfTarget(to)} to match the board?` +
        " The build directory will be emptied.";
    return confirm(root, text, `Change to ${chipNameOfTarget(to)}`);
}

/** Asks a yes-or-no question in a modal dialog; true when `label` was clicked. */
function confirm(root: HTMLElement, question: string, label: string): Promise<boolean> {
    const dialog = document.createElement("dialog");
    dialog.className = "idfweb-device-dialog";

    const text = document.createElement("p");
    text.textContent = question;

    const cancel = button("Cancel", () => dialog.close("cancel"));
    const change = button(label, () => dialog.close("change"));
    change.classList.add("idfweb-device-button-primary");
    const buttons = document.createElement("div");
    buttons.className = "idfweb-device-dialog-buttons";
    buttons.append(cancel, change);

    dialog.append(text, buttons);
    root.append(dialog);
    return new Promise((resolve) => {
        dialog.addEventListener("close", () => {
            dialog.remove();
            resolve(dialog.returnValue === "change");
        });
        dialog.showModal();
        change.focus();
    });
}

/** Initializes the target selection dropdown with default auto target. */
function targetSelect(): HTMLSelectElement {
    const select = document.createElement("select");
    select.className = "idfweb-device-select";
    select.title = "The chip to build for";
    select.append(option(AUTO, `auto (${chipNameOfTarget(autoTarget())})`));
    return select;
}

/** The configure preset dropdown, hidden until a project has presets. */
function presetSelect(): HTMLSelectElement {
    const select = document.createElement("select");
    select.className = "idfweb-device-select";
    select.title = "The CMake preset to build with, from CMakePresets.json";
    select.hidden = true;
    return select;
}

/** Populates target select dropdown with supported and preview targets. */
function fillTargets(select: HTMLSelectElement, targets: IdfTargets): void {
    select.replaceChildren(option(AUTO, `auto (${chipNameOfTarget(autoTarget())})`));
    for (const target of targets.supported) select.append(option(target, chipNameOfTarget(target)));
    if (targets.preview.length > 0) {
        const group = document.createElement("optgroup");
        group.label = "preview";
        for (const target of targets.preview) group.append(option(target, chipNameOfTarget(target)));
        select.append(group);
    }
}

function hasOption(select: HTMLSelectElement, value: string): boolean {
    return Array.from(select.options).some((element) => element.value === value);
}

function option(value: string, label: string): HTMLOptionElement {
    const element = document.createElement("option");
    element.value = value;
    element.textContent = label;
    return element;
}

/** Controls that stay together in the page's toolbar. */
function group(...children: HTMLElement[]): HTMLElement {
    const element = document.createElement("span");
    element.className = "idfweb-device-group";
    element.append(...children);
    return element;
}

function glyph(icon: string): HTMLSpanElement {
    const element = document.createElement("span");
    element.className = `codicon codicon-${icon}`;
    return element;
}

function button(label: string, onClick: () => void, icon?: string): HTMLButtonElement {
    const element = document.createElement("button");
    element.type = "button";
    element.className = "idfweb-device-button";
    if (icon !== undefined) element.append(glyph(icon));
    element.append(label);
    element.addEventListener("click", onClick);
    return element;
}

/** A toolbar button that is only an icon, as VS Code's title actions are. */
export function iconButton(icon: string, title: string, onClick: () => void): HTMLButtonElement {
    const element = document.createElement("button");
    element.type = "button";
    element.className = `idfweb-device-button idfweb-icon-button codicon codicon-${icon}`;
    element.title = title;
    element.setAttribute("aria-label", title);
    element.addEventListener("click", onClick);
    return element;
}

function message(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
}
